import type { Messages } from "./en";
import { opsDe } from "./ops.de";
import { partnerDe } from "./partner.de";

export const de: Messages = {
  common: {
    save: "Speichern",
    saving: "Speichern...",
    saved: "Gespeichert",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    add: "Hinzufügen",
    remove: "Entfernen",
    close: "Schliessen",
    back: "Zurück",
    search: "Suchen",
    retry: "Erneut versuchen",
    copy: "Kopieren",
    copied: "Kopiert",
    continue: "Weiter",
    optional: "optional",
    never: "Nie",
    none: "Keine",
    export: "Exportieren",
    import: "Importieren",
    name: "Name",
    email: "E-Mail",
    role: "Rolle",
    status: "Status",
    actions: "Aktionen",
    loading: "Wird geladen...",
    page: "{{from}} bis {{to}} von {{total}}",
    previous: "Zurück",
    next: "Weiter",
    view: "Ansehen",
  },
  nav: {
    overview: "Übersicht",
    contacts: "Kontakte",
    lists: "Listen",
    people: "Personen",
    sync: "Synchronisation",
    activity: "Aktivität",
    import: "Import",
    settings: "Einstellungen",
    handbook: "Handbuch",
    logout: "Abmelden",
    theme: "Dunkles Design",
    language: "Sprache",
    support: "Support-Zugriff auf {{org}}. Jede Aktion wird in der Aktivität des Kunden protokolliert.",
    exitSupport: "Support-Zugriff beenden",
  },
  roles: { admin: "Administrator", editor: "Listen-Editor" },
  notifications: {
    title: "Benachrichtigungen",
    description: "Welche E-Mails Kontaktfix an {{email}} sendet.",
    product: {
      title: "Hinweise zum Betrieb",
      hint: "Zum Beispiel, wenn jemand Neues keine Kontakte erhalten kann.",
    },
    lifecycle: {
      title: "Tipps für den Einstieg",
      hint: "Wenige E-Mails mit dem nächsten sinnvollen Schritt, jede höchstens einmal.",
    },
    updates: { title: "Neuigkeiten", hint: "Neue Funktionen, wenige Male im Jahr." },
    transactional:
      "E-Mails, die Sie für den Dienst brauchen, kommen immer: Einladungen, eine unterbrochene Verbindung zu Microsoft 365, ablaufende Zertifikate, Testphase, Abonnement und Zahlung.",
  },
  unsubscribe: {
    title: "Abbestellen",
    confirm: "Sie erhalten keine E-Mails der Art «{{category}}» mehr.",
    button: "Abbestellen",
    done: "Erledigt. Sie erhalten keine E-Mails der Art «{{category}}» mehr.",
    invalid:
      "Dieser Link ist nicht gültig. Verwenden Sie den Link aus der neusten E-Mail, oder ändern Sie die Einstellungen nach der Anmeldung.",
    manage: "Alle Benachrichtigungen verwalten",
  },
  bugReport: {
    button: "Problem melden",
    title: "Problem melden",
    description: "Beschreiben Sie, was nicht funktioniert hat. Wir schauen uns jede Meldung an.",
    tried: "Was wollten Sie tun?",
    happened: "Was ist stattdessen passiert?",
    severity: "Wie stark stört es Sie?",
    severities: { minor: "Wenig", annoying: "Es stört", blocking: "Ich kann nicht weiterarbeiten" },
    privacy:
      "Seite, Browser und die letzten Fehlermeldungen werden automatisch angehängt. Ihr Name und Ihre E-Mail-Adresse werden nicht weitergegeben.",
    send: "Meldung senden",
    sent: "Danke. Ihre Meldung wurde gesendet.",
  },
  maintenance: {
    title: "Wartung",
    body: "Kontaktfix wird gerade aktualisiert und ist gleich wieder da. Diese Seite lädt sich selbst neu.",
    delivery: "Kontakte, die schon in Outlook sind, bleiben dort.",
  },
  login: {
    title: "Bei Kontaktfix anmelden",
    subtitle: "Mit Ihrem Microsoft-Arbeitskonto. Kein zusätzliches Passwort.",
    microsoft: "Mit Microsoft anmelden",
    hosted: "Gehostet in der Schweiz",
    partner: "IT-Partner?",
    partnerSignIn: "Hier anmelden",
    partnerSignUp: "Hier registrieren",
    errors: {
      not_configured: "Die Microsoft-Anmeldung ist auf diesem Server noch nicht eingerichtet.",
      state: "Die Anmeldung ist abgelaufen. Bitte erneut versuchen.",
      access_denied: "Die Anmeldung wurde abgebrochen.",
      default: "Die Anmeldung ist fehlgeschlagen. Bitte erneut versuchen.",
    },
  },
  onboarding: {
    createTitle: "Organisation einrichten",
    createText:
      "Sie sind die erste Person aus Ihrer Firma. Erstellen Sie die Organisation, Sie werden deren Administrator. Danach verbinden Sie Microsoft 365.",
    orgName: "Name der Organisation",
    language: "Sprache für verteilte Kontakte",
    create: "Organisation erstellen",
    noAccessTitle: "Noch kein Zugang",
    noAccessText:
      "Ihre Firma nutzt Kontaktfix bereits. Bitten Sie einen Administrator, Sie unter Einstellungen, Mitglieder hinzuzufügen.",
    signedInAs: "Angemeldet als {{email}}",
    claimText:
      "Sind Sie Globaler Administrator Ihres Microsoft 365? Dann können Sie die Administratorrechte selbst übernehmen, zum Beispiel wenn bisher nur Ihr IT-Partner Kontaktfix betreut hat.",
    claim: "Administratorrechte übernehmen",
  },
  home: {
    title: "Übersicht",
    gettingStarted: "Erste Schritte",
    welcome: "Willkommen, {{name}}",
    subtitle: "In wenigen Schritten von der Anmeldung zu gemeinsamen Kontakten in Outlook.",
    stats: {
      contacts: "Kontakte",
      lists: "Listen",
      recipients: "Empfangende Personen",
      delivered: "Verteilte Kontakte",
    },
    steps: {
      connect: {
        title: "Microsoft 365 verbinden",
        text: "Kontaktfix einmalig freigeben oder Ihre eigene App-Registrierung verbinden.",
        action: "Verbinden",
      },
      lists: {
        title: "Kontaktliste erstellen",
        text: "Zum Beispiel Lieferanten oder Notfallnummern.",
        action: "Liste erstellen",
      },
      contacts: {
        title: "Kontakte erfassen",
        text: "Von Hand oder per Import aus Excel, CSV oder vCard.",
        action: "Importieren",
      },
      assign: {
        title: "Liste zuweisen",
        text: "Microsoft 365 Gruppen oder Personen auswählen, die sie erhalten.",
        action: "Zuweisen",
      },
      sync: {
        title: "Erste Verteilung",
        text: "Die Kontakte erscheinen in Outlook, getrennt von privaten Kontakten.",
        action: "Zur Synchronisation",
      },
      phone: {
        title: "Kontakte auf dem Handy",
        text: "In Outlook mobile Kontakte speichern einschalten, für Anrufernamen.",
        action: "Anleitung",
      },
    },
    done: "Erledigt",
    recent: "Letzte Aktivität",
    allActivity: "Alle Aktivitäten",
  },
  contacts: {
    title: "Kontakte",
    description: "Alle Geschäftskontakte Ihrer Organisation. Kontakte in Listen werden an Outlook verteilt.",
    new: "Neuer Kontakt",
    search: "Name, Firma, E-Mail oder Telefon suchen",
    allLists: "Alle Listen",
    empty: "Noch keine Kontakte",
    emptyText: "Erfassen Sie den ersten Kontakt oder importieren Sie eine Tabelle.",
    noResults: "Keine Kontakte gefunden.",
    columns: {
      name: "Name",
      company: "Firma",
      phone: "Telefon",
      email: "E-Mail",
      lists: "Listen",
      updated: "Geändert",
    },
    selected: "{{n}} ausgewählt",
    pageSelected: "Alle {{n}} Kontakte auf dieser Seite sind ausgewählt.",
    selectAll: "Alle {{n}} Kontakte auswählen",
    allSelected: "Alle {{n}} Kontakte sind ausgewählt.",
    allSelectedExcept: "{{n}} von {{total}} Kontakten sind ausgewählt.",
    clearSelection: "Auswahl aufheben",
    addToList: "Zu Liste hinzufügen",
    removeFromList: "Aus Liste entfernen",
    deleteSelected: "Löschen",
    deleteTitle_one: "{{n}} Kontakt löschen?",
    deleteTitle_other: "{{n}} Kontakte löschen?",
    deleteText:
      "Sie werden aus allen Listen und aus jedem Outlook entfernt, an das sie verteilt wurden. Dies kann nicht rückgängig gemacht werden.",
    deleted: "Gelöscht",
    readOnly: "Nur ansehen",
    readOnlyHint: "Sie können Kontakte bearbeiten, die in einer Ihrer Listen sind.",
    created: "Kontakt erstellt",
    updated: "Kontakt gespeichert",
    addedToList_one: "{{n}} Kontakt hinzugefügt",
    addedToList_other: "{{n}} Kontakte hinzugefügt",
    removedFromList_one: "{{n}} Kontakt entfernt",
    removedFromList_other: "{{n}} Kontakte entfernt",
    sharedWarning: "Auch in {{lists}}. Änderungen gelten dort ebenfalls.",
    managedNote: "Kontakte werden wenige Minuten nach dem Speichern an Outlook verteilt.",
    tabs: { details: "Details", history: "Verlauf" },
    sections: {
      person: "Person",
      company: "Firma",
      email: "E-Mail",
      phone: "Telefon",
      address: "Adresse",
      more: "Weiteres",
      lists: "Listen",
    },
    fields: {
      title: "Titel",
      givenName: "Vorname",
      middleName: "Zweiter Vorname",
      surname: "Nachname",
      companyName: "Firma",
      department: "Abteilung",
      jobTitle: "Funktion",
      nickName: "Rufname",
      profession: "Beruf",
      officeLocation: "Standort / Büro",
      manager: "Vorgesetzte Person",
      assistantName: "Assistenz",
      imAddress: "Chat-Adresse (Teams)",
      email1: "E-Mail",
      email2: "E-Mail 2",
      email3: "E-Mail 3",
      businessPhone1: "Telefon geschäftlich",
      businessPhone2: "Telefon geschäftlich 2",
      mobilePhone: "Mobile",
      homePhone1: "Telefon privat",
      homePhone2: "Telefon privat 2",
      street: "Strasse",
      postalCode: "PLZ",
      city: "Ort",
      state: "Kanton",
      country: "Land",
      website: "Webseite",
      notes: "Notizen",
    },
    addressTypes: { businessAddress: "Geschäftlich", homeAddress: "Privat", otherAddress: "Weitere" },
    addAddress: "Adresse hinzufügen",
    moreEmails: "E-Mail hinzufügen",
    morePhones: "Telefon hinzufügen",
    listsHint: "Der Kontakt wird an alle verteilt, die eine dieser Listen erhalten.",
    listRequired: "Wählen Sie mindestens eine Ihrer Listen.",
    photo: "Foto",
    uploadPhoto: "Foto hochladen",
    removePhoto: "Foto entfernen",
    photoSaved: "Foto gespeichert",
    validation: {
      invalid_phone: "Keine gültige Telefonnummer. Internationales Format, z. B. +41 44 123 45 67.",
      invalid_email: "Keine gültige E-Mail-Adresse.",
      invalid_url: "Keine gültige Webadresse.",
      name_or_company_required: "Geben Sie einen Namen oder eine Firma ein.",
    },
    deliveredTo: "An {{count}} Personen verteilt",
  },
  lists: {
    title: "Listen",
    description:
      "Listen gruppieren Kontakte nach Zweck. Weisen Sie eine Liste Gruppen oder Personen zu, und sie erhalten deren Kontakte.",
    new: "Neue Liste",
    empty: "Noch keine Listen",
    emptyText: "Erstellen Sie eine Liste wie Lieferanten, erfassen Sie Kontakte und weisen Sie sie zu.",
    columns: {
      name: "Name",
      contacts: "Kontakte",
      recipients: "Empfangend",
      editors: "Editoren",
      updated: "Geändert",
    },
    form: {
      name: "Name",
      namePlaceholder: "z. B. Lieferanten",
      description: "Beschreibung",
      color: "Farbe",
      kind: "Art der Liste",
      kinds: {
        manual: "Eigene Kontakte",
        directory: "Mitarbeitende aus Microsoft 365",
        file: "Datei-Import (geplant)",
      },
      kindHint: {
        manual: "Sie pflegen die Kontakte in Kontaktfix oder importieren sie.",
        directory:
          "Kontaktfix übernimmt die Mitarbeitenden aus Microsoft 365 und hält sie aktuell. Gepflegt wird in Microsoft 365.",
        file: "Kontaktfix holt eine CSV- oder Excel-Datei regelmässig ab, zum Beispiel einen nächtlichen Export aus Ihrer Branchen- oder Buchhaltungssoftware. Gepflegt wird in der Quelle.",
      },
    },
    file: {
      url: "Adresse der Datei",
      urlHint:
        "https:// oder sftp://, zum Beispiel ein nächtlicher Export. Nur öffentlich erreichbare Server.",
      auth: "Anmeldung",
      authTypes: {
        none: "Keine",
        basic: "Benutzername und Passwort",
        bearer: "Token (Authorization: Bearer)",
        password: "Benutzername und Passwort",
        key: "Benutzername und SSH-Schlüssel",
      },
      username: "Benutzername",
      password: "Passwort",
      token: "Token",
      privateKey: "Privater SSH-Schlüssel",
      secretHint: "Wird verschlüsselt gespeichert und nie wieder angezeigt.",
      secretStored: "Gespeichert. Leer lassen, um es zu behalten.",
      schedule: "Abholen",
      schedules: { hourly: "stündlich", daily: "täglich" },
      deletion: "Kontakte, die nicht mehr in der Datei sind",
      deletions: {
        after_days: "nach {{count}} Tagen entfernen",
        immediately: "sofort entfernen",
        never: "behalten",
      },
      deletionDays: "Tage bis zum Entfernen",
      deletionHint:
        "Schützt vor einem fehlerhaften Export: Fehlt ein Kontakt nur kurz, bleibt er auf den Handys.",
      read: "Datei lesen",
      readHint: "Lesen Sie die Datei einmal, um die Spalten zuzuordnen.",
      readResult: "{{rows}} Zeilen, {{columns}} Spalten gelesen.",
      mapping: "Spalten zuordnen",
      keyColumn: "Eindeutige Spalte",
      keyColumnHint:
        "Erkennt einen Kontakt beim nächsten Abholen wieder, zum Beispiel eine Kunden- oder Adressnummer.",
      keyAuto: "Automatisch (E-Mail, sonst Name und Firma)",
      status: "Zuletzt abgeholt: {{when}} · {{schedule}}",
      pending: "noch nicht",
      stats: "{{rows}} Zeilen: {{created}} neu, {{updated}} geändert, {{removed}} entfernt.",
      missing_one: "{{count}} Kontakt fehlt in der Datei und wird nach {{days}} Tagen entfernt.",
      missing_other: "{{count}} Kontakte fehlen in der Datei und werden nach {{days}} Tagen entfernt.",
      skipped_one: "{{count}} Zeile übersprungen (ungültig oder doppelt).",
      skipped_other: "{{count}} Zeilen übersprungen (ungültig oder doppelt).",
      error: "Letzter Versuch fehlgeschlagen: {{error}}. Die Kontakte bleiben unverändert.",
      fetchNow: "Jetzt abholen",
      fetched: "Datei abgeholt.",
      reasons: {
        https_or_sftp: "Nur https:// und sftp:// sind möglich",
        host_not_found: "Server nicht gefunden",
        address_not_allowed: "Dieser Server ist nicht öffentlich erreichbar",
        connection_failed: "Verbindung fehlgeschlagen",
        timeout: "Der Server hat nicht rechtzeitig geantwortet",
        access_denied: "Zugriff verweigert, prüfen Sie die Anmeldung",
        http_error: "Der Server meldet einen Fehler",
        too_many_redirects: "Zu viele Weiterleitungen",
        file_not_found: "Datei nicht gefunden",
        file_too_large: "Die Datei ist grösser als 15 MB",
        host_key_changed:
          "Der SSH-Schlüssel des Servers hat sich geändert. Speichern Sie die Adresse neu, wenn das gewollt ist",
        empty_file: "Die Datei ist leer",
        too_many_rows: "Die Datei hat zu viele Zeilen",
        unreadable_file: "Die Datei kann nicht gelesen werden",
        no_valid_contacts: "Keine gültigen Kontakte in der Datei",
        organization_contact_limit: "Das Kontaktlimit Ihrer Organisation wäre überschritten",
        recipient_limit: "Einzelne Personen würden zu viele Kontakte erhalten",
        credentials_unavailable: "Zugangsdaten können auf diesem Server nicht gespeichert werden",
      },
    },
    directory: {
      scope: "Wer kommt auf die Liste?",
      scopeAll: "Alle Personen in Microsoft 365",
      scopeGroups: "Mitglieder bestimmter Gruppen",
      groups: "Gruppen",
      addGroup: "Gruppe hinzufügen",
      filters: "Nur Personen mit",
      filtersHint: "Mehrere Werte mit Komma trennen. Leer lassen für alle.",
      filter: {
        companyName: "Firma",
        department: "Abteilung",
        officeLocation: "Standort / Büro",
        country: "Land",
        mailDomain: "Mail-Domain",
      },
      fieldsTitle: "Welche Angaben gehen aufs Handy?",
      fields: {
        jobTitle: "Funktion",
        department: "Abteilung",
        companyName: "Firma",
        officeLocation: "Standort / Büro",
        email: "E-Mail",
        businessPhones: "Telefon geschäftlich",
        mobilePhone: "Mobile",
        businessAddress: "Adresse geschäftlich",
        photo: "Profilbild",
      },
      mobileHint: "Oft eine private Nummer. Nur einschalten, wenn alle damit einverstanden sind.",
      include: "Zusätzlich aufnehmen",
      includeHint: "Zum Beispiel ein freigegebenes Postfach wie Support oder Empfang.",
      exclude: "Nie aufnehmen",
      excludeHint: "Zum Beispiel die Geschäftsleitung oder Personen mit privater Nummer in Microsoft 365.",
      addPerson: "Person oder Postfach hinzufügen",
      status:
        "Diese Liste wird aus Microsoft 365 gefüllt und alle 10 Minuten aktualisiert. Zuletzt: {{when}}.",
      pending: "in Kürze",
      error: "Letzte Aktualisierung fehlgeschlagen: {{error}}",
      excludedCount_one: "{{count}} Person nicht auf der Liste",
      excludedCount_other: "{{count}} Personen nicht auf der Liste",
      reasons: {
        guest: "Gast",
        disabled: "Konto deaktiviert",
        no_mail: "Keine E-Mail-Adresse",
        hidden: "In Microsoft 365 aus dem Adressbuch ausgeblendet",
        filtered: "Passt nicht zum Filter",
        excluded: "Von Ihnen ausgeschlossen",
      },
    },
    created: "Liste erstellt",
    updated: "Liste gespeichert",
    deleteTitle: "Liste {{name}} löschen?",
    deleteText:
      "Die Kontakte bleiben in Ihrer Organisation, werden aber aus dem Outlook aller entfernt, die sie nur über diese Liste erhalten haben.",
    deleted: "Liste gelöscht",
    tabs: { contacts: "Kontakte", assignments: "Zugewiesen an", editors: "Editoren", history: "Verlauf" },
    addContacts: "Kontakte hinzufügen",
    addContactsTitle: "Kontakte zu {{name}} hinzufügen",
    assignments: {
      title: "Wer diese Liste erhält",
      text: "Gruppenzuweisungen folgen der Gruppe automatisch: Wer dazukommt, erhält die Liste, wer geht, verliert sie.",
      add: "Zuweisen",
      search: "Gruppen und Personen in Microsoft 365 suchen",
      group: "Gruppe",
      user: "Person",
      members_one: "{{count}} Mitglied",
      members_other: "{{count}} Mitglieder",
      missing: "Nicht mehr in Microsoft 365",
      empty: "Noch nicht zugewiesen",
      emptyText: "Niemand erhält diese Liste. Weisen Sie sie einer Gruppe wie Alle Mitarbeitenden zu.",
      added: "Zugewiesen. Die Verteilung beginnt innerhalb einer Minute.",
      removed: "Zuweisung entfernt",
      noResults: "Keine Gruppen oder Personen gefunden.",
    },
    impact: {
      seats_one: "Dadurch kommt {{people}} neue Person dazu, das sind {{amount}} mehr pro Monat.",
      seats_other: "Dadurch kommen {{people}} neue Personen dazu, das sind {{amount}} mehr pro Monat.",
      title: "Liste an {{name}} zuweisen",
      summary_one: "{{people}} Person erhält danach bis zu {{contacts}} Kontakte.",
      summary_other: "{{people}} Personen erhalten danach je bis zu {{contacts}} Kontakte.",
      duration:
        "Die erste Verteilung dauert etwa {{from}} bis {{to}}. Microsoft begrenzt, wie schnell Kontakte in ein Postfach geschrieben werden dürfen. Danach werden nur noch Änderungen übertragen, das geht in Sekunden.",
      phone:
        "Auf dem Handy erscheinen die Kontakte, sobald Outlook sie synchronisiert hat. Bei vielen Kontakten kann das nach der Verteilung nochmals einige Minuten dauern.",
      warning:
        "Das sind sehr viele Kontakte für ein Handy. Prüfen Sie, ob alle Personen wirklich alle Listen brauchen.",
      overLimit:
        "Das ist mehr als das Limit von {{limit}} Kontakten pro Person. Verkleinern Sie die Listen oder weisen Sie sie weniger Personen zu. Für ein höheres Limit kontaktieren Sie uns.",
      minutes_one: "{{count}} Minute",
      minutes_other: "{{count}} Minuten",
      hours_one: "{{count}} Stunde",
      hours_other: "{{count}} Stunden",
    },
    editors: {
      title: "Listen-Editoren",
      text: "Editoren pflegen die Kontakte dieser Liste, ohne Administrator zu sein.",
      none: "Keine Editoren. Nur Administratoren können diese Liste ändern.",
      manage: "Editoren wählen",
      saved: "Editoren gespeichert",
      noMembers: "Fügen Sie zuerst unter Einstellungen, Mitglieder Kolleginnen und Kollegen hinzu.",
    },
    recipientsCount_one: "{{count}} Person",
    recipientsCount_other: "{{count}} Personen",
    readOnly: "Sie können diese Liste ansehen. Ändern können sie nur ihre Editoren und Administratoren.",
  },
  people: {
    title: "Personen",
    description: "Alle, die Kontakte erhalten, und warum.",
    empty: "Noch erhält niemand Kontakte",
    emptyText: "Weisen Sie eine Liste einer Gruppe oder Person zu, um die Verteilung zu starten.",
    search: "Personen suchen",
    columns: {
      person: "Person",
      status: "Status",
      lists: "Listen",
      contacts: "Kontakte",
      lastSync: "Letzte Verteilung",
    },
    receives: "Erhält",
    direct: "Direkt zugewiesen",
    viaGroup: "Über Gruppe {{group}}",
  },
  carddav: {
    title: "Handy ohne Outlook (CardDAV)",
    description:
      "Für Handys ohne Outlook: Die Kontakte-App des Handys liest die Kontakte dieser Person schreibgeschützt über CardDAV.",
    none: "Noch kein Zugang fürs Handy.",
    active: "Zugang {{username}} seit {{date}}, zuletzt genutzt {{used}}.",
    create: "Zugang erstellen",
    renew: "Neues Passwort",
    remove: "Zugang entfernen",
    removed: "Zugang fürs Handy entfernt",
    removeTitle: "Zugang fürs Handy von {{name}} entfernen?",
    removeText: "Die Kontakte verschwinden beim nächsten Abgleich vom Handy. Outlook ist nicht betroffen.",
    createdText:
      "Geben Sie diese Angaben der Person oder Ihrem MDM. Das Passwort wird nur jetzt angezeigt und nicht lesbar gespeichert.",
    server: "Server",
    username: "Benutzername",
    password: "Passwort",
    profile: "iPhone-Profil herunterladen",
    setup:
      "iPhone und iPad: Profil öffnen und unter Einstellungen > Allgemein > VPN und Geräteverwaltung installieren, oder mit Ihrem MDM verteilen. Android: In DAVx5 ein CardDAV-Konto mit Server, Benutzername und Passwort hinzufügen.",
    twice:
      "Speichert Outlook auf diesem Handy die Kontakte bereits, nutzen Sie nur eines von beiden, sonst erscheinen Kontakte doppelt. Änderungen auf dem Handy bleiben nicht erhalten.",
  },
  status: {
    pending: "Ausstehend",
    syncing: "Wird verteilt",
    ok: "Aktuell",
    error: "Fehler",
    unreachable: "Nicht erreichbar",
    removing: "Wird entfernt",
  },
  reasons: {
    no_mailbox: "Kein Exchange Online Postfach",
    out_of_scope: "Ausserhalb der erlaubten Postfächer",
    consent_missing: "Verbindung zu Microsoft 365 fehlt",
    throttled: "Microsoft bremst Anfragen, wird wiederholt",
    guest: "Gastkonto, kann keine Kontakte erhalten",
    cancelled: "Verteilung gestoppt, geht mit «Jetzt synchronisieren» weiter",
    unknown: "Unerwarteter Fehler",
  },
  sync: {
    title: "Synchronisation",
    description: "Verteilstatus über alle Postfächer. Änderungen werden automatisch innert Minuten verteilt.",
    syncNow: "Jetzt synchronisieren",
    syncRequested: "Synchronisation gestartet",
    lastSuccess: "Letzte Verteilung",
    recipients: "Personen",
    delivered: "Kontakte in Postfächern",
    problems: "Brauchen Aufmerksamkeit",
    problemsEmpty: "Alle sind erreichbar.",
    upToDate: "Aktuell",
    attention_one: "{{count}} Person braucht Aufmerksamkeit",
    attention_other: "{{count}} Personen brauchen Aufmerksamkeit",
    remaining_one: "noch etwa {{count}} Minute",
    remaining_other: "noch etwa {{count}} Minuten",
    inProgress: "Wird verteilt...",
    runs: "Letzte Durchläufe",
    noRuns: "Noch keine Durchläufe.",
    columns: {
      started: "Gestartet",
      trigger: "Auslöser",
      progress: "Fortschritt",
      changes: "Änderungen",
      errors: "Fehler",
    },
    triggers: {
      change: "Änderung",
      manual: "Manuell",
      schedule: "Nächtliche Prüfung",
      directory: "Gruppenmitglieder",
      setup: "Einrichtung",
      disconnect: "Trennen",
    },
    run: {
      title: "Durchlauf",
      showChanges: "Geänderte Kontakte anzeigen",
      status: { running: "Läuft", done: "Abgeschlossen", failed: "Fehlgeschlagen", cancelled: "Gestoppt" },
      stop: "Stoppen",
      stopTitle: "Durchlauf stoppen?",
      stopText:
        "Wartende Postfächer werden übersprungen, laufende hören nach dem aktuellen Block von höchstens 20 Kontakten auf. Bereits Verteiltes bleibt. Die gestoppten Postfächer werden erst wieder verteilt mit «Jetzt synchronisieren», bei einer Änderung an Kontakten oder in der nächtlichen Prüfung.",
      duration: { s: "{{s}} s", ms: "{{m}} Min. {{s}} s", hm: "{{h}} h {{m}} Min." },
      mailboxesDone: "{{done}}/{{total}} Postfächer",
      writes: "{{done}} von {{total}} Schreibvorgängen",
      mailboxCount: {
        running_one: "{{count}} läuft",
        running_other: "{{count}} laufen",
        queued_one: "{{count}} wartet",
        queued_other: "{{count}} warten",
        done_one: "{{count}} fertig",
        done_other: "{{count}} fertig",
        failed_one: "{{count}} fehlgeschlagen",
        failed_other: "{{count}} fehlgeschlagen",
        superseded_one: "{{count}} von späterem Durchlauf übernommen",
        superseded_other: "{{count}} von späterem Durchlauf übernommen",
        cancelled_one: "{{count}} gestoppt",
        cancelled_other: "{{count}} gestoppt",
      },
      mailboxStatus: {
        queued: "Wartet",
        running: "Läuft",
        done: "Fertig",
        failed: "Fehlgeschlagen",
        superseded: "Übernommen",
        cancelled: "Gestoppt",
      },
      created: "Neu",
      updated: "Geändert",
      deleted: "Entfernt",
      errors: "Fehler",
      requests: "Anfragen an Microsoft",
      photos_one: "{{count}} Foto",
      photos_other: "{{count}} Fotos",
      throttled: "{{duration}} gebremst",
      throttledShort: ", {{duration}} von Microsoft gebremst",
      tabs: { mailboxes: "Postfächer", changes: "Änderungen", log: "Protokoll" },
      columns: {
        person: "Person",
        status: "Status",
        progress: "Fortschritt",
        requests: "Anfragen",
        duration: "Dauer",
        contact: "Kontakt",
        change: "Änderung",
        reason: "Grund",
        mailboxes: "Postfächer",
      },
      ops: { all: "Alle", create: "Neu", update: "Geändert", delete: "Entfernt" },
      reasons: {
        changed: "Angaben geändert",
        edited_in_outlook: "In Outlook verändert, zurückgesetzt",
        not_desired: "Nicht mehr in den Listen der Person",
        duplicate: "Doppelt im Postfach",
        orphan: "Unbekannter Kontakt von Kontaktfix",
        moved: "Am alten Ort (Verteilart geändert)",
      },
      inMailboxes_one: "in {{count}} Postfach",
      inMailboxes_other: "in {{count}} Postfächern",
      failedIn_one: "in {{count}} fehlgeschlagen",
      failedIn_other: "in {{count}} fehlgeschlagen",
      errorsOnly: "Nur mit Fehlern",
      searchContact: "Kontakt suchen",
      noChanges: "Keine Änderungen.",
      unknownContact: "Unbekannter Kontakt",
      shown: "{{shown}} von {{total}} angezeigt",
      more: "Mehr anzeigen",
      levels: { all: "Alles", warn: "Warnungen und Fehler", error: "Nur Fehler" },
      live: "Live",
      noDetails:
        "Für diesen Durchlauf gibt es nur die Summen: Er lief vor der Umstellung auf die genaue Protokollierung.",
      noEvents: "Noch keine Einträge.",
      events: {
        run_started:
          "Durchlauf gestartet ({{trigger}}): {{mailboxes}} Postfächer zu prüfen, {{people}} Personen erhalten Kontakte.",
        mailbox_started:
          "{{name}}: soll {{contacts}} Kontakte haben, {{existing}} sind im Postfach. Plan: {{create}} neu, {{update}} ändern, {{delete}} entfernen.",
        mailbox_throttled:
          "{{name}}: Microsoft bremst, {{duration}} gewartet. Kontaktfix macht automatisch weiter.",
        contact_failed: "{{name}}: «{{label}}» ({{opText}}) fehlgeschlagen: {{error}}",
        mailbox_done:
          "{{name}}: fertig in {{duration}}. {{created}} neu, {{updated}} geändert, {{deleted}} entfernt, {{photos}} Fotos, {{errors}} Fehler, {{calls}} Anfragen{{throttled}}.",
        mailbox_failed: "{{name}}: abgebrochen nach {{duration}}. {{reason}}: {{detail}}",
        mailbox_superseded: "{{name}}: wird von einem späteren Durchlauf verteilt.",
        mailbox_interrupted:
          "{{name}}: unterbrochen (seit 20 Minuten keine Rückmeldung). Der nächste Durchlauf macht weiter.",
        run_cancelled: "Durchlauf gestoppt, {{skipped}} wartende Postfächer übersprungen.",
        mailbox_cancelled:
          "{{name}}: gestoppt nach {{duration}}. Bis dahin {{created}} neu, {{updated}} geändert, {{deleted}} entfernt.",
        run_finished:
          "Durchlauf beendet nach {{duration}}: {{created}} neu, {{updated}} geändert, {{deleted}} entfernt, {{errors}} Fehler, {{failed}} Postfächer fehlgeschlagen.",
      },
    },
    connectionMissing: "Microsoft 365 ist nicht verbunden, deshalb kann nichts verteilt werden.",
    connect: "Jetzt verbinden",
  },
  activity: {
    title: "Aktivität",
    description: "Wer hat was wann geändert.",
    empty: "Noch keine Aktivität.",
    system: "Kontaktfix",
    filters: {
      all: "Alle",
      contact: "Kontakte",
      list: "Listen",
      member: "Mitglieder",
      settings: "Einstellungen",
    },
    actions: {
      file_imported: "hat die Datei abgeholt: {{created}} neu, {{updated}} geändert, {{removed}} entfernt in",
      file_fetched: "hat die Datei abgeholt für",
      requested: "hat angefragt:",
      created: "hat erstellt:",
      updated: "hat geändert:",
      deleted: "hat gelöscht:",
      deleted_many: "hat {{count}} Kontakte gelöscht",
      photo_changed: "hat das Foto geändert von",
      photo_removed: "hat das Foto entfernt von",
      contacts_added: "hat {{count}} Kontakte hinzugefügt zu",
      contacts_removed: "hat {{count}} Kontakte entfernt aus",
      assigned: "hat {{target}} zugewiesen an",
      unassigned: "hat {{target}} entfernt von",
      editors_changed: "hat die Editoren geändert von",
      added: "hat hinzugefügt:",
      removed: "hat entfernt:",
      role_changed: "hat die Rolle geändert von",
      organization_created: "hat die Organisation erstellt",
      connected: "hat Microsoft 365 verbunden",
      consent_failed: "hat versucht, Microsoft 365 zu verbinden",
      disconnected: "hat Microsoft 365 getrennt",
      disconnect_started: "hat begonnen, Microsoft 365 zu trennen",
      disconnect_cancelled: "hat die Verbindung zu Microsoft 365 beibehalten",
      imported: "hat {{created}} neue und {{updated}} aktualisierte Kontakte importiert",
      sync_requested: "hat eine Synchronisation gestartet",
      sync_cancelled: "hat eine Synchronisation gestoppt",
      support_access_started: "hat den Support-Zugriff gestartet",
      support_access_ended: "hat den Support-Zugriff beendet",
      organization_suspended: "hat die Organisation gesperrt",
      organization_resumed: "hat die Organisation entsperrt",
      checkout_started: "hat den Abo-Abschluss geöffnet",
      subscription_changed: "hat den Abo-Status geändert",
      trial_extended: "hat die Testphase verlängert",
      linked: "hat den IT-Partner verknüpft:",
      link_requested: "hat Zugriff angefragt als IT-Partner:",
      approved: "hat den IT-Partner bestätigt:",
      declined: "hat die Anfrage abgelehnt vom IT-Partner",
      unlinked: "hat den IT-Partner entfernt:",
      admin_claimed: "hat als Globaler Administrator die Administratorrechte übernommen",
      billed_to_partner: "hat die Abrechnung auf den IT-Partner umgestellt",
      billed_to_customer: "hat die Abrechnung zurück auf die Organisation umgestellt",
      carddav_created: "hat einen Handy-Zugang (CardDAV) erstellt für",
      carddav_removed: "hat den Handy-Zugang (CardDAV) entfernt von",
      api_key_created: "hat den API-Schlüssel erstellt",
      api_key_revoked: "hat den API-Schlüssel widerrufen",
      webhook_created: "hat den Webhook hinzugefügt",
      webhook_removed: "hat den Webhook entfernt",
    },
    entity: {
      contact: "Kontakt",
      list: "Liste",
      member: "Mitglied",
      settings: "Einstellungen",
    },
    showChanges: "Änderungen anzeigen",
    empty_value: "leer",
  },
  import: {
    title: "Kontakte importieren",
    description:
      "Excel, CSV oder vCard. Sie sehen eine Vorschau mit Duplikat-Warnungen, bevor etwas gespeichert wird.",
    steps: { upload: "Hochladen", map: "Spalten", review: "Prüfen" },
    drop: "Datei hierher ziehen oder klicken zum Auswählen",
    formats: "Excel (.xlsx), CSV oder vCard (.vcf), bis 25'000 Kontakte",
    parsing: "Datei wird gelesen...",
    mapTitle: "Spalten zuordnen",
    mapText: "Wir haben die Felder anhand der Spaltennamen erkannt. Bitte prüfen und anpassen.",
    column: "Spalte in der Datei",
    sample: "Beispiel",
    field: "Feld in Kontaktfix",
    ignore: "Ignorieren",
    reviewTitle: "{{count}} Kontakte prüfen",
    valid: "{{count}} bereit",
    invalid: "{{count}} mit Fehlern (werden übersprungen)",
    duplicates: "{{count}} mögliche Duplikate",
    duplicateOf: "Ähnlich wie {{name}} ({{reason}})",
    reasons: { email: "gleiche E-Mail", phone: "gleiche Telefonnummer", name: "gleicher Name" },
    inFile: "Doppelt in der Datei",
    actionCreate: "Neu erstellen",
    actionUpdate: "Bestehenden aktualisieren",
    actionSkip: "Überspringen",
    targetLists: "Zu Listen hinzufügen",
    targetListsHint: "Importierte Kontakte kommen in diese Listen.",
    submit_one: "{{count}} Kontakt importieren",
    submit_other: "{{count}} Kontakte importieren",
    done: "{{created}} erstellt, {{updated}} aktualisiert",
    saving: "{{done}} von {{total}} gespeichert...",
    resume: "Weiter importieren ({{done}} von {{total}} gespeichert)",
    partial: "{{done}} Kontakte sind bereits gespeichert. Klicken Sie erneut, um den Rest zu importieren.",
    fullName: "Ganzer Name",
    fields: {
      businessStreet: "Strasse geschäftlich",
      businessPostalCode: "PLZ geschäftlich",
      businessCity: "Ort geschäftlich",
      businessState: "Kanton geschäftlich",
      businessCountry: "Land geschäftlich",
      homeStreet: "Strasse privat",
      homePostalCode: "PLZ privat",
      homeCity: "Ort privat",
      homeCountry: "Land privat",
    },
  },
  apiAccess: {
    title: "API",
    docs: "API-Dokumentation",
    adminOnly: "Administratoren erstellen API-Schlüssel und Webhooks für die REST-API von Kontaktfix.",
    scope: { read: "Lesen", write: "Lesen und schreiben" },
    scopeHint: {
      read: "Kontakte, Listen und den Verteilstatus lesen.",
      write: "Zusätzlich Kontakte und Listen erstellen, ändern und löschen.",
    },
    keys: {
      title: "API-Schlüssel",
      description:
        "Damit pflegen Ihre eigenen Skripte und Systeme Kontakte über die REST-API. Jede Änderung mit einem Schlüssel steht mit seinem Namen im Verlauf.",
      create: "API-Schlüssel erstellen",
      empty: "Noch keine API-Schlüssel.",
      name: "Name",
      nameHint: "Wofür der Schlüssel ist, z. B. ERP-Abgleich.",
      access: "Zugriff",
      lists: "Listen",
      allLists: "Alle Listen",
      someLists: "Nur ausgewählte Listen",
      someListsHint:
        "Der Schlüssel arbeitet wie ein Listenbearbeiter: Er sieht und ändert nur Kontakte in diesen Listen und kann weder Kontakte löschen noch Listen verwalten.",
      expiry: "Läuft ab",
      days_one: "Nach {{count}} Tag",
      days_other: "Nach {{count}} Tagen",
      noExpiry: "Nie",
      expired: "Abgelaufen",
      meta: "erstellt am {{created}} von {{by}} · zuletzt verwendet: {{used}} · läuft ab: {{expires}}",
      createdTitle: "Kopieren Sie den API-Schlüssel jetzt",
      createdText:
        "Der Schlüssel wird nur dieses eine Mal angezeigt. Bewahren Sie ihn in einem Passwortmanager oder im Geheimnisspeicher Ihres Systems auf. Kontaktfix speichert nur einen Fingerabdruck davon.",
      revoke: "Widerrufen",
      revokeTitle: "Schlüssel {{name}} widerrufen?",
      revokeText:
        "Systeme mit diesem Schlüssel funktionieren sofort nicht mehr. Das lässt sich nicht rückgängig machen.",
      revoked: "API-Schlüssel widerrufen",
    },
    webhooks: {
      title: "Webhooks",
      description:
        "Kontaktfix ruft Ihre HTTPS-Adresse auf, wenn sich etwas ändert, signiert, damit Sie prüfen können, dass der Aufruf von uns kommt. In der Regel innert einer Minute.",
      add: "Webhook hinzufügen",
      empty: "Noch keine Webhooks.",
      url: "Adresse",
      urlHint: "Nur HTTPS. Ein Token in der Adresse ist erlaubt.",
      httpsRequired: "Die Adresse muss mit https:// beginnen",
      eventsLabel: "Ereignisse",
      events_one: "{{count}} Ereignis",
      events_other: "{{count}} Ereignisse",
      last: "letzte Zustellung {{when}}",
      never: "noch nichts zugestellt",
      pending_one: "{{count}} wartet auf erneuten Versuch",
      pending_other: "{{count}} warten auf erneuten Versuch",
      ok: "OK",
      failed: "Fehlgeschlagen",
      test: "Testereignis senden",
      testQueued: "Testereignis eingereiht. Es kommt innert einer Minute an.",
      createdTitle: "Kopieren Sie das Signaturgeheimnis jetzt",
      createdText: "Ihr Server prüft damit den Header webhook-signature. Es wird nur jetzt angezeigt.",
      remove: "Entfernen",
      removeTitle: "Webhook entfernen?",
      removeText: "{{url}} erhält keine Ereignisse mehr.",
      removed: "Webhook entfernt",
    },
    eventText: {
      contact_created: "Ein Kontakt wurde erstellt.",
      contact_updated: "Ein Kontakt oder sein Foto wurde geändert.",
      contact_deleted: "Ein Kontakt wurde gelöscht.",
      list_created: "Eine Liste wurde erstellt.",
      list_updated: "Name, Beschreibung oder Farbe einer Liste wurde geändert.",
      list_deleted: "Eine Liste wurde gelöscht.",
      list_contacts_changed: "Kontakte wurden einer Liste hinzugefügt oder daraus entfernt.",
      sync_completed: "Ein Verteillauf ist abgeschlossen.",
      delivery_failed: "Die Verteilung in ein Postfach ist fehlgeschlagen.",
      recipient_unreachable: "Eine Person ist nicht erreichbar, z. B. kein Postfach.",
    },
  },
  integrations: {
    available: "Verfügbar",
    availableText: "Im Preis enthalten, auch während der Testphase.",
    createList: "Liste anlegen",
    built: {
      directory: {
        name: "Microsoft 365 Verzeichnis",
        text: "Mitarbeitende als Kontakte, automatisch aktuell. Änderungen erscheinen innert etwa einer Minute.",
      },
      file: {
        name: "Datei-Import (CSV, Excel)",
        text: "Holt eine Datei stündlich oder täglich per HTTPS oder SFTP ab. Funktioniert mit fast jeder Software, die Adressen exportieren kann.",
      },
    },
    onRequest: "Auf Anfrage",
    onRequestText:
      "Direkte Anbindungen bauen wir, sobald Kunden sie brauchen. Fragen Sie an, wir melden uns. Bis dahin hilft der Datei-Import.",
    onRequestBadge: "Auf Anfrage",
    request: "Anfragen",
    requested: "Angefragt",
    requestSent: "Danke, wir melden uns bei Ihnen.",
    connectors: {
      bexio: { name: "bexio", text: "Kontakte und Kontaktgruppen aus bexio." },
      abacus: { name: "Abacus", text: "Adressen aus Abacus, zum Beispiel Kunden und Lieferanten." },
      sage: { name: "Sage 50 / Sage 200", text: "Adressen aus der Schweizer Sage-Buchhaltung." },
      hubspot: { name: "HubSpot", text: "Kontakte und Firmen aus dem HubSpot CRM, gefiltert nach Listen." },
      pipedrive: { name: "Pipedrive", text: "Personen und Organisationen aus Pipedrive." },
      dynamics: {
        name: "Microsoft Dynamics 365",
        text: "Kontakte aus Dynamics 365 Sales oder Business Central.",
      },
      zapier: { name: "Zapier und Make", text: "Kontakte aus über 6000 Apps per Automatisierung." },
    },
  },
  settings: {
    title: "Einstellungen",
    tabs: {
      general: "Allgemein",
      members: "Mitglieder",
      microsoft: "Microsoft 365",
      delivery: "Verteilung",
      integrations: "Integrationen",
      api: "API",
      billing: "Abo",
      partner: "Partner",
    },
    partner: {
      title: "IT-Partner",
      text: "Ein IT-Partner kann Kontaktfix mit eigenen Konten für Sie betreuen. Jede Aktion des Partners erscheint im Verlauf mit dem Namen des Partners. Sie können den Zugriff jederzeit entziehen.",
      code: "Partnercode",
      codeHint: "8 Zeichen, von Ihrem IT-Partner.",
      link: "Zugriff geben",
      linked: "Der Partner hat jetzt Zugriff.",
      empty: "Kein IT-Partner hat Zugriff.",
      pending: "Bittet um Zugriff",
      active: "Hat Zugriff",
      since: "seit {{date}}",
      requested: "angefragt am {{date}}",
      approve: "Zustimmen",
      decline: "Ablehnen",
      revoke: "Zugriff entziehen",
      revokeTitle: "Zugriff für {{name}} entziehen?",
      revokeText:
        "Die Techniker des Partners verlieren sofort den Zugriff. Ihre Kontakte und Einstellungen bleiben, wie sie sind.",
      approved: "Zugriff bestätigt.",
      revoked: "Zugriff entzogen.",
      declined: "Anfrage abgelehnt.",
      partnerOnly: "Partner verknüpfen oder bestätigen nur Ihre eigenen Administratoren.",
    },
    general: {
      orgName: "Name der Organisation",
      language: "Sprache",
      languageHint: "Wird für Ordnernamen und Notizen in Outlook verwendet.",
      tenant: "Microsoft 365 Tenant-ID",
    },
    members: {
      title: "Mitglieder",
      text: "Administratoren verwalten alles. Listen-Editoren sehen und pflegen nur ihre eigenen Listen und deren Kontakte. Mitglieder melden sich mit ihrem Microsoft-Arbeitskonto an.",
      add: "Mitglied hinzufügen",
      addTitle: "Kollegin oder Kollegen hinzufügen",
      search: "Im Microsoft 365 Verzeichnis suchen",
      lastLogin: "Letzte Anmeldung",
      notYet: "Noch nicht angemeldet",
      editorOf: "Bearbeitet {{lists}}",
      removeTitle: "{{name}} entfernen?",
      removeText:
        "Die Person verliert den Zugang zu Kontaktfix. Kontakte in ihrem Outlook sind davon nicht betroffen.",
      added: "Mitglied hinzugefügt",
      you: "Sie",
    },
    microsoft: {
      title: "Verbindung zu Microsoft 365",
      status: { granted: "Verbunden", none: "Nicht verbunden", error: "Verbindungsproblem" },
      checked: "Zuletzt geprüft {{time}}",
      connect: "Microsoft 365 verbinden",
      reconnect: "Erneut freigeben",
      test: "Verbindung prüfen",
      connectText:
        "Kontaktfix schreibt Kontakte in Postfächer und liest Benutzer und Gruppen. Mails, Kalender und private Kontakte werden nie gelesen. Wählen Sie, wie ein Microsoft 365 Administrator die Verbindung herstellt.",
      method: { consent: "Kontaktfix-App freigeben", own_app: "Eigene App-Registrierung" },
      methodText: {
        consent:
          "Ein Klick. Ein Globaler Administrator gibt Kontaktfix auf der Zustimmungsseite von Microsoft frei; Microsoft legt dann den Service Principal in Ihrem Tenant an.",
        own_app:
          "Für IT-Teams, die den Service Principal selbst verwalten wollen. Sie erstellen die App in Microsoft Entra mit Zertifikat oder geheimem Clientschlüssel und können sie jederzeit widerrufen. Mit unserem Skript in etwa 5 Minuten.",
      },
      setUpOwnApp: "Einrichten",
      editOwnApp: "App-Registrierung ändern",
      clientId: "Anwendungs-ID (Client-ID)",
      certificateUntil: "Zertifikat gültig bis {{date}}",
      secretStored: "Geheimer Clientschlüssel {{hint}} verschlüsselt gespeichert",
      disconnect: "Trennen",
      disconnectTitle: "Microsoft 365 trennen?",
      disconnectText:
        "Kontaktfix entfernt zuerst alle verteilten Kontakte aus sämtlichen Postfächern und vergisst danach die Verbindung. Persönliche Kontakte bleiben unberührt. Listen und Kontakte bleiben in Kontaktfix erhalten. Um den Zugriff vollständig zu entziehen, löschen Sie danach die Kontaktfix-App in Microsoft Entra.",
      cleanup: {
        title_one: "Trennen läuft: Kontakte werden aus {{count}} Postfach entfernt",
        title_other: "Trennen läuft: Kontakte werden aus {{count}} Postfächern entfernt",
        text: "Kontaktfix trennt die Verbindung automatisch, sobald alle Postfächer bereinigt sind, meist innert Minuten.",
        blocked:
          "Die Verbindung zu Microsoft 365 funktioniert nicht, deshalb kann nichts entfernt werden. Genehmigen Sie Kontaktfix erneut (oder korrigieren Sie Ihre App-Registrierung), um abzuschliessen, oder trennen Sie, ohne Kontakte zu entfernen.",
        keep: "Verbindung behalten",
        force: "Trennen ohne Entfernen",
        forceTitle: "Trennen, ohne Kontakte zu entfernen?",
        forceText:
          "Bereits verteilte Kontakte bleiben in den Postfächern und werden nicht mehr aktualisiert. Mitarbeitende müssen sie von Hand löschen.",
      },
      permissions: "Angeforderte Berechtigungen",
      perms: {
        contacts: "Contacts.ReadWrite: verteilte Kontakte in Postfächer schreiben",
        users: "User.Read.All: Personen auswählen und deren Postfach prüfen",
        groups: "GroupMember.Read.All: Gruppen auswählen und deren Mitgliedern folgen",
      },
      groupsPending: {
        consent:
          "Änderungen an Personen erscheinen innert etwa einer Minute. Für Gruppen braucht Kontaktfix eine neue Berechtigung (Group.Read.All): Klicken Sie auf «Erneut freigeben» und bestätigen Sie. Bis dahin prüft Kontaktfix Gruppen alle 10 Minuten.",
        own_app:
          "Änderungen an Personen erscheinen innert etwa einer Minute. Damit auch Gruppenänderungen sofort ankommen, ergänzen Sie in Ihrer App-Registrierung die Anwendungsberechtigung Group.Read.All und erteilen die Zustimmung. Bis dahin prüft Kontaktfix Gruppen alle 10 Minuten.",
      },
      consentOk: "Microsoft 365 ist verbunden. Die erste Verteilung startet jetzt.",
      consentError: "Die Verbindung hat nicht funktioniert: {{reason}}",
      notConfigured:
        "Auf diesem Server ist noch keine Microsoft App-Registrierung eingerichtet. Bitte wenden Sie sich an den Betreiber von Kontaktfix.",
      hardenedTitle: "Zugriff auf ausgewählte Postfächer beschränken (optional)",
      hardenedText:
        "Standardmässig erlaubt Microsoft einer freigegebenen App den Zugriff auf alle Postfächer. Um Kontaktfix auf die Mitglieder einer Gruppe zu beschränken, führt ein Exchange-Administrator dieses Skript einmal aus. Änderungen können bis zu 2 Stunden dauern.",
    },
    delivery: {
      title: "Wo die Kontakte in Outlook landen",
      folder: "Eigener Ordner",
      folderText:
        "Kontaktfix legt einen eigenen Kontaktordner an, getrennt von den privaten Kontakten. Das klassische Outlook zeigt ihn als Ordner, das neue Outlook und Outlook im Web als Kategorie mit dem Ordnernamen.",
      main: "Hauptordner Kontakte",
      mainText:
        "Die Kontakte kommen in den normalen Ordner Kontakte, neben die privaten Kontakte. Jedes Outlook und jedes Handy zeigt sie, Anrufernamen funktionieren. Zur Unterscheidung schalten Sie unten die Kategorien ein.",
      folderName: "Ordnername",
      folderNameHint:
        "Ordnername im klassischen Outlook, Name der Kategorie im neuen Outlook und in Outlook im Web.",
      noteText: "Notiz in jedem Kontakt",
      noteHint: "Informiert Mitarbeitende, dass Änderungen in Outlook überschrieben werden.",
      categories: "Liste als Outlook-Kategorie anzeigen",
      categoriesHint:
        "Jeder Kontakt erhält die Namen seiner Listen als Kategorie, so können Mitarbeitende in Outlook nach Liste filtern. Private Kontakte erhalten keine Kategorie.",
      saved: "Verteilung gespeichert. Die Postfächer werden innert Minuten aktualisiert.",
    },
  },
  phone: {
    title: "Kontakte auf dem Handy",
    description:
      "Mit einer Einstellung in der Outlook App erscheinen die verteilten Kontakte im Adressbuch des Handys, und bei Anrufen wird der Name angezeigt.",
    iosTitle: "iPhone",
    androidTitle: "Android",
    ios: [
      "Outlook App öffnen und auf das Profilbild tippen.",
      "Einstellungen antippen, dann das Arbeitskonto.",
      "Kontakte speichern einschalten und den Zugriff auf Kontakte erlauben.",
    ],
    android: [
      "Outlook App öffnen und auf das Profilbild tippen.",
      "Einstellungen antippen, dann das Arbeitskonto.",
      "Kontakte synchronisieren einschalten und den Zugriff auf Kontakte erlauben.",
    ],
    itTitle: "Für die IT: für alle einschalten",
    itText:
      "Mit Microsoft Intune eine App-Konfigurationsrichtlinie für Outlook erstellen und die Kontaktsynchronisation aktivieren. Firmenrichtlinien können sie auch blockieren, dann werden keine Anrufernamen angezeigt.",
    noteTitle: "Gut zu wissen",
    notes: [
      "Änderungen in Kontaktfix erreichen das Handy, sobald Outlook synchronisiert, meist innert Minuten.",
      "Änderungen auf dem Handy werden nicht zurückgespielt und bei der nächsten Aktualisierung überschrieben.",
      "Wenn Kontakte in Outlook erscheinen, aber nicht auf dem Handy, ist die Einstellung aus oder durch eine Richtlinie blockiert.",
    ],
  },

  ownApp: {
    back: "Microsoft 365",
    title: "Eigene App-Registrierung verbinden",
    description:
      "Sie erstellen eine App-Registrierung in Ihrem Microsoft Entra Tenant und behalten die volle Kontrolle über Service Principal und Anmeldedaten. Kontaktfix erhält nur die drei Berechtigungen unten.",
    status: { connected: "Verbunden", notConnected: "Nicht verbunden" },
    recommended: "Empfohlen",
    unavailable:
      "Eigene App-Registrierungen sind auf diesem Server nicht aktiviert. Bitte wenden Sie sich an den Betreiber von Kontaktfix.",
    step1: {
      title: "Anmeldeart der App wählen",
      text: "Damit holt Kontaktfix Zugriffstokens für Ihren Tenant.",
    },
    kind: {
      certificate: "Zertifikat",
      certificateText:
        "Kontaktfix hat ein Zertifikat für Ihre Organisation erstellt. Sie laden nur den öffentlichen Teil hoch; nichts Geheimes wird kopiert. Gültig bis {{date}}.",
      secret: "Geheimer Clientschlüssel",
      secretText:
        "Sie erstellen in Entra einen geheimen Schlüssel und fügen ihn hier ein. Kontaktfix speichert ihn verschlüsselt. Schlüssel laufen nach höchstens 2 Jahren ab.",
    },
    step2: {
      title: "App-Registrierung erstellen",
      text: "Führen Sie unser Skript aus oder klicken Sie sich selbst durch Microsoft Entra. Beides führt zum selben Ergebnis.",
    },
    way: { script: "PowerShell-Skript", manual: "Manuell in Microsoft Entra" },
    script: {
      text: "Führen Sie es in PowerShell 7 als Globaler Administrator aus. Es erstellt die App, fügt die Berechtigungen hinzu, erteilt die Administratorzustimmung und lädt das Zertifikat hoch.",
      download: "Herunterladen",
      outputCert:
        "Am Ende gibt das Skript die Anwendungs-ID (Client-ID) aus. Fügen Sie sie in Schritt 3 ein.",
      outputSecret:
        "Am Ende gibt das Skript die Anwendungs-ID (Client-ID) und den geheimen Clientschlüssel aus. Fügen Sie beides in Schritt 3 ein.",
    },
    manual: {
      register: "Öffnen Sie in Microsoft Entra die App-Registrierungen und wählen Sie Neue Registrierung.",
      name: "Name",
      accountTypes:
        "Unterstützte Kontotypen: Nur Konten in diesem Organisationsverzeichnis. Keine Umleitungs-URI.",
      openEntra: "App-Registrierungen öffnen",
      permissions:
        "Öffnen Sie API-Berechtigungen, Berechtigung hinzufügen, Microsoft Graph, Anwendungsberechtigungen, und fügen Sie diese drei hinzu:",
      permission: "Berechtigung",
      type: "Typ",
      purpose: "Wofür",
      application: "Anwendung",
      grant:
        "Wählen Sie unter API-Berechtigungen Administratorzustimmung für Ihre Organisation erteilen. Alle drei zeigen dann Gewährt.",
      uploadCert:
        "Öffnen Sie Zertifikate & Geheimnisse, Zertifikate, Zertifikat hochladen, und laden Sie diese Datei hoch:",
      createSecret:
        "Öffnen Sie Zertifikate & Geheimnisse, Geheime Clientschlüssel, Neuer geheimer Clientschlüssel (24 Monate). Kopieren Sie sofort den Wert (nicht die Geheimnis-ID); Entra zeigt ihn nur einmal.",
      copyId: "Kopieren Sie auf der Übersichtsseite die Anwendungs-ID (Client-ID).",
    },
    purpose: {
      contacts: "Verteilte Kontakte in Postfächer schreiben",
      users: "Personen auswählen und deren Postfach prüfen",
      groups: "Gruppen auswählen und deren Mitgliedern folgen",
    },
    downloadCert: "kontaktfix.cer herunterladen",
    thumbprint: "SHA-256-Fingerabdruck",
    step3: {
      title: "Prüfen und verbinden",
      text: "Kontaktfix meldet sich mit Ihrer App an und prüft jede Berechtigung. Gespeichert wird erst, wenn alle Prüfungen bestanden sind.",
    },
    tenantId: "Verzeichnis-ID (Tenant-ID)",
    tenantIdHint: "Die App muss in diesem Tenant registriert sein.",
    clientId: "Anwendungs-ID (Client-ID)",
    secret: "Wert des geheimen Clientschlüssels",
    secretHint: "Die Spalte Wert in Entra, nicht die Geheimnis-ID.",
    secretKeep: "Leer lassen, um den gespeicherten Schlüssel {{hint}} zu behalten.",
    verify: "Prüfen und verbinden",
    verifyAgain: "Erneut prüfen",
    retrying:
      "Microsoft braucht manchmal einige Minuten, um neue Berechtigungen anzuwenden. Neue Prüfung in {{seconds}} s.",
    checkNow: "Jetzt prüfen",
    connected: "Microsoft 365 ist verbunden. Die erste Verteilung startet jetzt.",
    done: "Fertig",
    check: {
      title: "Prüfung",
      status: "Status",
      running: "Wird geprüft",
      waiting: "Ausstehend",
      ok: "Bestätigt",
      failed: "Fehlgeschlagen",
      credential: {
        certificate: "Anmeldung mit dem Zertifikat",
        secret: "Anmeldung mit dem geheimen Clientschlüssel",
      },
    },
    reason: {
      app_not_found:
        "In Ihrem Tenant gibt es keine App mit dieser Client-ID. Prüfen Sie die ID auf der Übersichtsseite der App.",
      tenant_not_found: "Der Tenant wurde nicht gefunden.",
      invalid_secret:
        "Der geheime Clientschlüssel ist falsch. Fügen Sie den Wert ein, nicht die Geheimnis-ID.",
      secret_expired: "Der geheime Clientschlüssel ist abgelaufen. Erstellen Sie in Entra einen neuen.",
      certificate_not_uploaded:
        "Die App akzeptiert das Kontaktfix-Zertifikat nicht. Laden Sie kontaktfix.cer unter Zertifikate & Geheimnisse hoch.",
      kontaktfix_app:
        "Das ist die Kontaktfix-App selbst. Geben Sie die Client-ID Ihrer eigenen App ein oder nutzen Sie die Freigabe mit einem Klick.",
      not_granted:
        "Nicht gewährt. Fügen Sie sie als Anwendungsberechtigung hinzu und erteilen Sie die Administratorzustimmung.",
      not_effective:
        "Gewährt, aber von Microsoft noch nicht angewendet. Das erledigt sich meist innerhalb weniger Minuten.",
      unknown: "Microsoft hat einen unerwarteten Fehler gemeldet.",
    },
  },
  billing: {
    title: "Abo",
    partnerPays: "{{partner}} bezahlt Kontaktfix als Ihr IT-Partner. Sie brauchen kein eigenes Abo.",
    partnerEnds:
      "{{partner}} bezahlt bis am {{date}}. Danach brauchen Sie ein eigenes Abo; schliessen Sie es jetzt ab, ist die erste Zahlung am {{date}} fällig.",
    partnerMissing:
      "{{partner}} bezahlt Kontaktfix als Ihr IT-Partner, hat die Zahlung aber noch nicht eingerichtet. Bis dahin werden keine Kontakte zugestellt. Bitte melden Sie sich bei Ihrem Partner.",
    ownEnding: "Ihr eigenes Abo endet am {{date}}. Danach bezahlt Ihr IT-Partner.",
    subscribe: "Abo abschliessen",
    portal: "Rechnungen und Zahlung",
    subscribed: "Vielen Dank. Ihr Abo ist aktiv.",
    waitingForStripe: "Zahlung wird bei Stripe bestätigt …",
    readOnly:
      "Ihre Testphase ist abgelaufen. Kontakte können nur angesehen werden, bis eine Administratorin oder ein Administrator ein Abo abschliesst.",
    sidebarTrial_one: "Testphase: noch {{count}} Tag",
    sidebarTrial_other: "Testphase: noch {{count}} Tage",
    states: {
      disabled: "Nicht eingerichtet",
      trial_pending: "Testphase nicht gestartet",
      trial: "Testphase",
      active: "Aktiv",
      past_due: "Zahlung fehlgeschlagen",
      expired: "Testphase abgelaufen",
      canceled: "Beendet",
    },
    status: {
      disabled:
        "Die Abrechnung ist auf diesem Server nicht eingerichtet. Alles funktioniert ohne Einschränkung.",
      trial_pending:
        "Ihre kostenlose Testphase von {{days}} Tagen beginnt, sobald die ersten Kontakte in einem Postfach ankommen. Keine Kreditkarte nötig.",
      trial_one: "Kostenlose Testphase bis {{date}} (noch {{count}} Tag). Keine Kreditkarte nötig.",
      trial_other: "Kostenlose Testphase bis {{date}} (noch {{count}} Tage). Keine Kreditkarte nötig.",
      active: "Ihr Abo ist aktiv. Nächste Rechnung am {{date}}.",
      activeEnding: "Ihr Abo ist gekündigt und endet am {{date}}.",
      past_due:
        "Die letzte Zahlung ist fehlgeschlagen. Stripe versucht es automatisch erneut. Bitte prüfen Sie Ihre Zahlungsmethode.",
      expired:
        "Die kostenlose Testphase ist am {{date}} abgelaufen. Kontakte werden nicht mehr verteilt und können erst nach Abschluss eines Abos wieder erfasst und bearbeitet werden.",
      canceled:
        "Ihr Abo ist beendet. Kontakte werden nicht mehr verteilt und können erst nach einem neuen Abo wieder erfasst und bearbeitet werden.",
    },
    users: "Personen mit Kontakten",
    usersHint: "Täglich gezählt",
    pricePerUser: "Preis pro Person",
    perUserMonth: "pro Person und Monat",
    estimate: "Total pro Monat",
    perMonth: "bei der aktuellen Anzahl Personen",
    volume_one: "Ab {{count}} Person bieten wir Mengenpreise an. Schreiben Sie an",
    volume_other: "Ab {{count}} Personen bieten wir Mengenpreise an. Schreiben Sie an",
    howTitle: "So wird abgerechnet",
    howUsers:
      "Sie zahlen für jede Person in Microsoft 365, die Kontakte erhält, direkt oder über eine Gruppe (auch verschachtelte Gruppen). Jede Person zählt einmal, egal wie viele Listen sie erhält. Gäste und Personen ohne Postfach zählen nicht.",
    howBilling:
      "Das Abo wird monatlich abgerechnet. Die Anzahl Personen wird täglich aktualisiert; ändert sie sich, wird die Differenz auf der nächsten Rechnung tageweise verrechnet oder gutgeschrieben. Sie können jederzeit unter Rechnungen und Zahlung kündigen.",
    howTrial:
      "Die kostenlose Testphase von {{days}} Tagen beginnt mit der ersten Verteilung. Schliessen Sie während der Testphase ein Abo ab, ist die erste Zahlung erst an ihrem Ende fällig. Endet sie ohne Abo, wird die Verteilung pausiert und Kontakte können nur noch angesehen werden. Kontakte, die bereits in Outlook sind, bleiben dort.",
    banner: {
      trialEnding_one: "Ihre kostenlose Testphase endet in {{count}} Tag.",
      trialEnding_other: "Ihre kostenlose Testphase endet in {{count}} Tagen.",
      expired:
        "Ihre kostenlose Testphase ist abgelaufen. Kontakte werden nicht mehr verteilt und können nicht erfasst oder bearbeitet werden.",
      canceled:
        "Ihr Abo ist beendet. Kontakte werden nicht mehr verteilt und können nicht erfasst oder bearbeitet werden.",
      pastDue: "Eine Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsmethode.",
      askAdmin: "Bitte wenden Sie sich an eine Administratorin oder einen Administrator.",
      updatePayment: "Zahlung aktualisieren",
    },
  },
  suspended: {
    title: "{{org}} ist gesperrt",
    text: "Kontaktfix hat diese Organisation gesperrt. Kontakte in Outlook bleiben, werden aber nicht mehr aktualisiert. Bitte wenden Sie sich an Kontaktfix.",
  },
  ops: opsDe,
  partner: partnerDe,
  errors: {
    generic: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
    forbidden: "Dafür fehlt Ihnen die Berechtigung.",
    admin_only: "Nur Administratoren können dies tun.",
    not_found: "Nicht gefunden.",
    list_name_taken: "Eine Liste mit diesem Namen existiert bereits.",
    already_assigned: "Bereits zugewiesen.",
    already_member: "Diese Person ist bereits Mitglied.",
    last_admin: "Die Organisation braucht mindestens einen Administrator.",
    list_required: "Wählen Sie mindestens eine Ihrer Listen.",
    not_editor_of_contact: "Sie können nur Kontakte in Ihren Listen bearbeiten.",
    not_editor_of_list: "Sie sind nicht Editor dieser Liste.",
    invalid_image: "Dieses Bild kann nicht gelesen werden.",
    empty_file: "Die Datei enthält keine Daten.",
    unsupported_format: "Bitte .xlsx, .csv oder .vcf verwenden.",
    recipient_limit:
      "Damit würden {{people}} Personen je bis zu {{contacts}} Kontakte erhalten, erlaubt sind {{limit}} pro Person. Verkleinern Sie die Listen oder weisen Sie sie weniger Personen zu.",
    organization_contact_limit:
      "Damit hätte Ihre Organisation {{contacts}} Kontakte, erlaubt sind {{limit}}. Für ein höheres Limit kontaktieren Sie uns.",
    directory_list: "Diese Liste wird aus Microsoft 365 gefüllt. Ändern Sie die Personen dort.",
    directory_contact: "Diese Person kommt aus Microsoft 365. Ändern Sie die Angaben dort.",
    file_list: "Diese Liste wird aus einer Datei gefüllt. Ändern Sie die Kontakte in der Quelle.",
    file_contact: "Dieser Kontakt kommt aus einer Datei. Ändern Sie die Angaben in der Quelle.",
    file_source_required: "Bitte geben Sie die Adresse der Datei an.",
    file_fetch_failed: "Die Datei konnte nicht gelesen werden.",
    credentials_unavailable: "Zugangsdaten können auf diesem Server nicht gespeichert werden.",
    selection_changed:
      "Die Kontakte haben sich inzwischen geändert, die Auswahl umfasst jetzt {{count}}. Bitte prüfen Sie sie und versuchen Sie es erneut.",
    directory_source_required: "Bitte wählen Sie, wer auf die Liste kommt.",
    too_many_rows:
      "Die Datei hat {{rows}} Zeilen, erlaubt sind {{max}}. Bitte teilen Sie die Datei auf und importieren Sie die Teile nacheinander.",
    organization_exists: "Ihre Organisation existiert bereits.",
    validation_failed: "Bitte prüfen Sie die markierten Felder.",
    directory_object_not_found: "In Microsoft 365 nicht gefunden.",
    not_configured: "Die Microsoft-Anmeldung ist nicht eingerichtet.",
    own_app_unavailable: "Eigene App-Registrierungen sind auf diesem Server nicht aktiviert.",
    organization_suspended: "Diese Organisation ist gesperrt.",
    unauthenticated: "Bitte melden Sie sich erneut an.",
    invalid_credentials: "E-Mail oder Passwort ist falsch.",
    too_many_attempts: "Zu viele Versuche. Warten Sie 15 Minuten und versuchen Sie es erneut.",
    invalid_code: "Der Code ist falsch oder wurde schon verwendet. Warten Sie auf den nächsten.",
    login_expired: "Die Anmeldung hat zu lange gedauert. Bitte neu beginnen.",
    elevation_required: "Bestätigen Sie mit einem Code aus Ihrer Authenticator-App.",
    setup_link_invalid: "Dieser Einrichtungslink funktioniert nicht mehr.",
    weak_password: "Das Passwort darf Ihren E-Mail-Namen nicht enthalten.",
    wrong_password: "Das aktuelle Passwort ist falsch.",
    last_operator: "Mindestens ein aktiver Betreiber muss bleiben.",
    admin_exists: "Ein Betreiber mit dieser E-Mail existiert bereits.",
    not_on_self: "Das ist bei Ihrem eigenen Konto nicht möglich.",
    confirmation_mismatch: "Der Name stimmt nicht überein.",
    not_connected: "Microsoft 365 ist nicht verbunden.",
    credentials_key_missing: "CREDENTIALS_KEY ist auf dem Server nicht gesetzt.",
    totp_not_started: "Scannen Sie zuerst den QR-Code.",
    support_session: "In einer Support-Sitzung nicht verfügbar.",
    subscription_required:
      "Ihre Testphase ist abgelaufen. Schliessen Sie unter Einstellungen → Abo ein Abo ab.",
    billing_not_configured: "Die Abrechnung ist auf diesem Server nicht eingerichtet.",
    partner_billed: "Ihr IT-Partner bezahlt Kontaktfix. Sie brauchen kein eigenes Abo.",
    already_subscribed: "Es gibt bereits ein aktives Abo.",
    partner_suspended: "Dieses Partnerkonto ist gesperrt. Bitte wenden Sie sich an Kontaktfix.",
    second_factor_required: "Richten Sie zuerst einen zweiten Faktor ein (Mein Konto).",
    no_customer_access: "Sie haben keinen Zugriff auf diesen Kunden. Fragen Sie Ihren Partner-Admin.",
    partner_admin_only: "Das dürfen nur Partner-Admins.",
    customer_admin_only: "Das dürfen nur die Administratoren des Kunden selbst.",
    partner_not_found: "Zu diesem Code wurde kein Partner gefunden.",
    partner_session: "In einer Partner-Sitzung nicht verfügbar.",
    partner_admin_has_full_access: "Partner-Admins haben Zugriff auf alle Kunden.",
    tenant_not_found: "Zu dieser Domain wurde kein Microsoft-365-Tenant gefunden.",
    already_linked: "Dieser Partner ist bereits verknüpft oder eine Anfrage ist offen.",
    last_partner_admin: "Mindestens ein Partner-Admin muss bleiben.",
    email_taken: "Für diese Adresse gibt es bereits ein Partnerkonto.",
    cannot_remove_self: "Sie können sich nicht selbst entfernen.",
    no_customer: "Es gibt noch keine Rechnungen.",
  },
};
